import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import * as dayjs from "dayjs";
import * as utc from "dayjs/plugin/utc";
import { MONTHS_ENUM } from "../../../common/Constants";
import { useGetToDoLocationAuditCounts, useGenerateBulkToDoLocationAuditReport } from "../../../../backend/masters";
import { useGetUserCompanies } from "../../../../backend/query";
import { ResponseModel } from "../../../../models/responseModel";
import { toast } from "react-toastify";
import { getUserDetails } from "../../../../backend/auth";
import { AxiosError } from "axios";
import PageLoader from "../../../shared/PageLoader";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import './VendorAuditBulkDownloadModal.css';
dayjs.extend(utc as any);

interface MonthYear {
    month: string;
    year: number;
}

const INCLUDE_HIERARCHY = {
    parent_company: {
        associate_companies: {
            company_location_mappings: {
                location: ['city']
            }
        }
    }
};

function LocationAuditBulkDownloadModal({ onSubmit, onCancel, previousFilters }: any) {
    const [company, setCompany] = useState<any>(null);
    const [associateCompany, setAssociateCompany] = useState<any>(null);
    const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [locations, setLocations] = useState<any>(null);
    const [selectedMonthYears, setSelectedMonthYears] = useState<MonthYear[]>([]);
    const [pickerRange, setPickerRange] = useState<any[]>([]);
    const [auditCounts, setAuditCounts] = useState<any>(null);
    const [isOpen, setIsOpen] = useState(false);
    const autoFetchTimerRef = useRef<any>(null);
    const user = getUserDetails();

    const { userCompanies, isFetching } = useGetUserCompanies({
        filters: { users_id_eq: user.user_id, is_active_eq: true },
        includes: INCLUDE_HIERARCHY,
    });

    const { getToDoLocationAuditCounts, fetchingAuditCounts } =
        useGetToDoLocationAuditCounts((response: ResponseModel) => {
            if (response instanceof AxiosError) {
                const data = (((response || {}).response || {}).data || {}).errors;
                toast.error(Object.values(data || {}).join(", "));
            } else {
                setAuditCounts(response.data);
            }
        }, errorCallback);

    const { generateBulkToDoLocationAuditReport, generatingAuditReport } =
        useGenerateBulkToDoLocationAuditReport((response: ResponseModel) => {
            if (response instanceof AxiosError) {
                const data = (((response || {}).response || {}).data || {}).errors;
                toast.error(Object.values(data || {}).join(", "));
            } else {
                toast.success("Preparing your ZIP. You will receive an e-mail when it's ready.");
                onSubmit();
                closeDrawer();
            }
        }, errorCallback);

    useEffect(() => {
        requestAnimationFrame(() => setIsOpen(true));
    }, []);

    useEffect(() => {
        if (!previousFilters || !userCompanies || userCompanies.length === 0) return;
        const prevCompany = previousFilters.company;
        const prevAC = previousFilters.associate_company;
        const prevLocation = previousFilters.location;

        if (prevCompany) {
            const raw = userCompanies.find((c: any) => c.id == prevCompany.value);
            if (raw) {
                const co = { value: raw.id, label: raw.name, company: raw };
                setCompany(co);
                const acs = (raw.associate_companies || []).map((ac: any) => ({
                    value: ac.id,
                    label: ac.name,
                    associate_company: ac,
                    locations: ac.locations,
                }));
                setAssociateCompanies(acs);

                if (prevAC) {
                    const matchedAC = acs.find((ac: any) => ac.value == prevAC.value);
                    if (matchedAC) {
                        setAssociateCompany(matchedAC);
                        const locs = (matchedAC.locations || []).map((l: any) => ({
                            value: l.id,
                            label: l.name,
                            location: l,
                        }));
                        setLocationOptions(locs);

                        if (prevLocation) {
                            const matchedLoc = locs.find((l: any) => l.value == prevLocation.value);
                            if (matchedLoc) setLocations([matchedLoc]);
                        }
                    }
                }
            }
        }
    }, [previousFilters, userCompanies]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!company || !associateCompany || selectedMonthYears.length === 0) {
            setAuditCounts(null);
            return;
        }
        if (autoFetchTimerRef.current) clearTimeout(autoFetchTimerRef.current);
        autoFetchTimerRef.current = setTimeout(() => {
            getToDoLocationAuditCounts(buildPayload() as any);
        }, 400);
        return () => {
            if (autoFetchTimerRef.current) clearTimeout(autoFetchTimerRef.current);
        };
    }, [company, associateCompany, selectedMonthYears, locations]); // eslint-disable-line react-hooks/exhaustive-deps

    function errorCallback() {
        toast.error(ERROR_MESSAGES.DEFAULT);
    }

    function closeDrawer() {
        setIsOpen(false);
        setTimeout(() => onCancel(), 280);
    }

    function onCompanyChange(e: any) {
        if (company && company.value == e.value) return;
        const raw = (userCompanies || []).find((c: any) => c.id == e.value);
        const acs = raw ? (raw.associate_companies || []).map((ac: any) => ({
            value: ac.id,
            label: ac.name,
            associate_company: ac,
            locations: ac.locations,
        })) : [];
        setCompany(e);
        setAssociateCompanies(acs);
        setAssociateCompany(null);
        setLocationOptions([]);
        setLocations(null);
        setSelectedMonthYears([]);
        setPickerRange([]);
        setAuditCounts(null);
    }

    function onAssociateCompanyChange(e: any) {
        if (associateCompany && associateCompany.value == e.value) return;
        const locs = (e.locations || []).map((l: any) => ({
            value: l.id,
            label: l.name,
            location: l,
        }));
        setAssociateCompany(e);
        setLocationOptions(locs);
        setLocations(null);
        setAuditCounts(null);
    }

    function expandMonthRange(start: any, end: any): MonthYear[] {
        const result: MonthYear[] = [];
        let m = start.month.number;
        let y = start.year;
        const endM = end.month.number;
        const endY = end.year;
        while (y < endY || (y === endY && m <= endM)) {
            result.push({ month: MONTHS_ENUM[m - 1], year: y });
            m++;
            if (m > 12) { m = 1; y++; }
        }
        return result;
    }

    function buildPayload() {
        const payload: any = {
            month_years: selectedMonthYears,
            location_ids: (locations || []).map((l: any) => (l || {}).value),
            auditor_id: getUserDetails().userid,
        };
        if ((company || {}).value) payload.company_id = company.value;
        if ((associateCompany || {}).value) payload.associate_company_id = associateCompany.value;
        if (selectedMonthYears.length === 1) {
            payload.month = selectedMonthYears[0].month;
            payload.year = String(selectedMonthYears[0].year);
        }
        return payload;
    }

    function download() {
        if (!company || !associateCompany || selectedMonthYears.length === 0) {
            toast.error("Please select Company, Associate Company and at least one Month–Year.");
            return;
        }
        generateBulkToDoLocationAuditReport(buildPayload() as any);
    }

    const canDownload = auditCounts &&
        auditCounts.completed_count > 0 &&
        !auditCounts.exceeds_download_limit;

    const companyOptions = (userCompanies || []).map((c: any) => ({ value: c.id, label: c.name, company: c }));

    return (
        <>
            <div className={`bulk-drawer-backdrop${isOpen ? ' open' : ''}`} />

            <div className={`bulk-drawer${isOpen ? ' open' : ''}`} role="dialog" aria-modal="true">

                {/* ── Head ── */}
                <div className="la-drawer-head">
                    <div className="la-drawer-title">Download Bulk Report</div>
                    <button className="la-drawer-close" onClick={closeDrawer} aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* ── Body (single scrollable zone) ── */}
                <div className="la-drawer-body">
                    <div className="la-drawer-field">
                        <label>Company Name</label>
                        <Select
                            options={companyOptions}
                            value={company}
                            onChange={onCompanyChange}
                            placeholder="Select company"
                            classNamePrefix="drawer-select"
                            isLoading={isFetching}
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Associate Company</label>
                        <Select
                            options={associateCompanies}
                            value={associateCompany}
                            onChange={onAssociateCompanyChange}
                            placeholder="Select associate company"
                            classNamePrefix="drawer-select"
                            isDisabled={!company}
                            isLoading={isFetching}
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Audit Month(s) and Year(s)</label>
                        <DatePicker
                            onlyMonthPicker
                            range
                            portal
                            zIndex={11200}
                            value={pickerRange}
                            onChange={(dates: any) => {
                                const arr = Array.isArray(dates) ? dates : (dates ? [dates] : []);
                                setPickerRange(arr);
                                if (arr.length === 2 && arr[0] && arr[1]) {
                                    setSelectedMonthYears(expandMonthRange(arr[0], arr[1]));
                                } else {
                                    setSelectedMonthYears([]);
                                }
                            }}
                            format="MMM, YYYY"
                            placeholder="Select month range"
                            disabled={!associateCompany}
                            editable={false}
                            inputClass="bulk-month-picker-input"
                        />
                    </div>

                    {fetchingAuditCounts && (
                        <div className="bulk-counts-loading">Fetching report counts…</div>
                    )}
                    {auditCounts && !fetchingAuditCounts && (
                        <>
                            <div className="bulk-audit-counts">
                                <div className="bulk-count-item">
                                    <div className="bulk-count-value">{auditCounts.completed_count}</div>
                                    <div className="bulk-count-label">Completed Audits</div>
                                </div>
                                <div className="bulk-count-divider" />
                                <div className="bulk-count-item">
                                    <div className="bulk-count-value">{auditCounts.incomplete_count}</div>
                                    <div className="bulk-count-label">Incomplete Audits</div>
                                </div>
                            </div>
                            {auditCounts.exceeds_download_limit && (
                                <div className="bulk-error-msg">
                                    Too many reports ({auditCounts.completed_count}). Max {auditCounts.download_limit} allowed.
                                </div>
                            )}
                            {auditCounts.completed_count === 0 && (
                                <div className="bulk-error-msg">
                                    Cannot generate reports as there are 0 completed audits.
                                </div>
                            )}
                        </>
                    )}

                    <div className="la-drawer-field">
                        <label>Locations</label>
                        <Select
                            options={locationOptions}
                            value={locations}
                            onChange={(e: any) => setLocations(e)}
                            placeholder="Select location"
                            classNamePrefix="drawer-select"
                            isDisabled={!associateCompany}
                            isLoading={isFetching}
                            isMulti
                        />
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="la-drawer-foot">
                    <button
                        className="la-drawer-download-btn"
                        onClick={download}
                        disabled={!canDownload || fetchingAuditCounts}
                    >
                        {canDownload
                            ? `Download Bulk Report (${auditCounts.completed_count} reports)`
                            : "Download Bulk Report"}
                    </button>
                </div>
            </div>

            {generatingAuditReport && <PageLoader />}
        </>
    );
}

export default LocationAuditBulkDownloadModal;
